import type { LicenceResponse, ListResponse } from '@shared/schemas';

/** Which management action is in flight, so the page can say what succeeded. */
export type ListMutationKind = 'rename' | 'transfer' | 'duplicate' | 'delete';

export type ListMutationStatus = 'idle' | 'pending' | 'succeeded' | 'failed';

/**
 * One action at a time, and only one.
 *
 * The dialog is modal and offers a single action per visit, so a second
 * mutation cannot start while one is running. `kind` survives into `succeeded`
 * because the page needs it to word the snackbar ("renamed" and "deleted" are
 * not the same sentence), and `name` with it, since a deleted list is gone from
 * `rows` by the time the message is written.
 */
export interface ListMutation {
  status: ListMutationStatus;
  kind: ListMutationKind | null;
  /** The list's name at the moment the action was taken. */
  name: string;
  error: string | null;
}

export const NO_MUTATION: ListMutation = {
  status: 'idle',
  kind: null,
  name: '',
  error: null,
};

/**
 * Redux state for the admin list index: a read-only listing until #551 gave
 * its pencil three management actions. `error` holds a human-readable message
 * when the load fails (surfaced by the table); a failed *mutation* is transient
 * and goes to a snackbar instead, never to that banner, or a failed rename would
 * leave a permanent error over a perfectly loaded table (PI-50).
 *
 * Every field is serialisable.
 */
export interface ListsState {
  rows: ListResponse[];
  loading: boolean;
  error: string | null;
  /** Options for the transfer picker. Loaded on demand, admin-only. */
  licences: LicenceResponse[];
  licencesLoading: boolean;
  mutation: ListMutation;
}

export const initialState: ListsState = {
  rows: [],
  loading: false,
  error: null,
  licences: [],
  licencesLoading: false,
  mutation: NO_MUTATION,
};
